import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Descriptions, Space, Button, Typography, Divider, Tag, Tooltip } from "antd";
import { CalendarOutlined, CreditCardOutlined, DollarOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusCircleTwoTone, ReloadOutlined, RetweetOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import HtmlParser from "react-html-parser";
import { ConvertcustomerGender, ConvertCustomerScheduleType, isValidDate, RenderPaymentMethods, repeatPhoneNumber } from "@/utils/helpers";
import TreatmentSessionForm from "./TreatmentSessions/TreatmentSessionForm";
import { checkPermission } from "@/utils/permission";
import CustomerEditForm from "../CustomerEditForm";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from 'react-router-dom';
import { getSpecificCustomer } from "@/redux/actions/customerActions";
import FamilyRelationshipsTable from "./FamilyRelationshipsTable ";
import HandlePhoneNumber from "@/components/HandlePhoneNumber";
import NumberFormat from "react-number-format";
import CustomerPayment from "../CustomerPayment";
import AdditionalCost from "../AdditionalCost";
import PhoneList from "../PhoneList";
import { getCustomerPaymentCustomer } from "@/redux/actions/CustomerPayment";
import { getAdditionalCostCustomer } from "@/redux/actions/AdditionalCost";
import PrintButton from "@/components/PrintButton/PrintButton";
import PrintCustomerProfile from "./PrintCustomerProfile";
import { getByCustomer } from "@/redux/actions/medicalRecordActions";
import PrintSurgeryCommitment from "./PrintSurgeryCommitment";
import PrintRepresentative from "./PrintRepresentative";
import PrintRefusa from "./PrintRefusa";

const { Text, Paragraph } = Typography;

export default function CustomerInfoTab(props) {

    const dispatch = useDispatch();
    const location = useLocation();

    const { id } = useParams();

    const d = props.data || {};
    const firstAppointment = props.data?.appointments?.[0] ?? {};

    const [formVisible, setFormVisible] = useState(false);
    const [isOpenEditForm, setFormVisibleEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [customerPaymentData, setCustomerPaymentData] = useState(null);
    const [isOpenPaymentMethod, setIsOpenPaymentMethod] = useState(false);
    const [additionalCostData, setAdditionalCostData] = useState(null);
    const [isOpenAdditionalCost, setIsOpenAdditionalCost] = useState(false);
    const [isOpenPhoneList, setIsOpenPhoneList] = useState(false);
    const [medicalRecordData, setMedicalRecordData] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);

    const allowCreateCustomer = checkPermission('customer', 'update');
    const canViewFinance = checkPermission('customer', 'view_finance');

    const [showMoney, setShowMoney] = useState(canViewFinance);

    useEffect(() => {
        if (!canViewFinance) {
            setShowMoney(false);
        }
    }, [canViewFinance]);



    const handleCancel = () => {
        setFormVisible(false);
    };

    const toggleOpenEditForm = (isOpenEditForm) => {
        setFormVisibleEdit(isOpenEditForm);
    }

    const handleReloadData = async () => {
        try {
            setIsLoading(true);
            await dispatch(getSpecificCustomer(id));
            console.log('Reload customer data');
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to reload data:', error);
        }
    };

    const onDetailPaymentMethod = (customer_id) => {
        dispatch(getCustomerPaymentCustomer(customer_id))
            .then((res) => {
                setIsOpenPaymentMethod(true);
                setCustomerPaymentData(res);
            })
            .catch((err) => {
                console.error('Lỗi khi lấy thông tin thanh toán:', err);
            });
    };

    const onDetailAdditionalCost = (customer_id) => {
        dispatch(getAdditionalCostCustomer(customer_id))
            .then((res) => {
                setIsOpenAdditionalCost(true);
                setAdditionalCostData(res);
            })
            .catch((err) => {
                console.error('Lỗi khi lấy thông tin thanh toán bổ sung:', err);
            });
    }

    const loadMedicalecords = useCallback(async () => {
        if (!d?.id) return;
        setIsDisabled(true);
        try {
            const result = await dispatch(getByCustomer({}, d.id));
            //console.log('result', result);
            const data = result?.data;

            if (data && Object.keys(data).length > 0) {
                const normalized = {
                    ...data
                };
                setMedicalRecordData(normalized);
            } else {
                setMedicalRecordData([]);
            }
        } catch (error) {
            console.error("Failed to load medical records:", error);
            setMedicalRecordData([]);
        } finally {
            setIsDisabled(false);
        }
    }, []);

    useEffect(() => {
        loadMedicalecords();
    }, [location.pathname]);

    const phones = d?.phones || [];
    const permission = checkPermission('customer', 'phonenumber');
    const inputPinCode = checkPermission('customer', 'input_pin_code');

    const noPhone = d?.no_phone === 1; // hoặc true

    const created_user = d?.created_user;

    const revenue = Number(d?.revenue || 0);
    const paid = Number(d?.total_amount_paid || 0);

    const paidPercentRaw = revenue > 0 ? (paid / revenue) * 100 : 0;
    const paidPercent = Math.round(paidPercentRaw * 10) / 10;

    let collectRateColor = '#1677ff'; // >= 100% (xanh dương)
    if (paidPercent < 50) {
        collectRateColor = '#fa8c16';   // tím đậm – thu kém (cảnh báo)
    } else if (paidPercent < 100) {
        collectRateColor = '#531dab';   // cam – đang thu
    }

    return (
        <div>
            <div>
                {/* Nút chức năng */}
                <div className="mt-4 mb-4">
                    <Row gutter={8}>
                        <Col>
                            <Space size={8}>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={() => toggleOpenEditForm(true)}
                                    disabled={!allowCreateCustomer}
                                >
                                    Cập nhật thông tin khách hàng
                                </Button>
                                {/* In bệnh án ngoại trú */}
                                <PrintButton disabled={isDisabled}
                                    printText="In bệnh án"
                                    tooltipText="Bệnh án ngoại trú răng miệng"
                                >
                                    <PrintCustomerProfile
                                        customer={d}
                                        data={medicalRecordData}
                                    />
                                </PrintButton>
                                {/* In cam kết phẫu thuật, thủ thuật */}
                                <PrintButton disabled={isDisabled}
                                    printText="In giấy cam kết"
                                    tooltipText="Giấy cam kết chấp thuận phẫu thuật, thủ thuật và gây mê hồi sức"
                                >
                                    <PrintSurgeryCommitment
                                        customer={d}
                                        data={{}}
                                    />
                                </PrintButton>

                                {/* In giấy đại diện người bệnh */}
                                <PrintButton disabled={isDisabled}
                                    printText="In giấy đại diện"
                                    tooltipText="Giấy đề nghị làm người đại diện cho người bệnh"
                                >
                                    <PrintRepresentative
                                        customer={d}
                                        data={{}}
                                    />
                                </PrintButton>

                                {/* In giấy từ chối điều trị */}
                                <PrintButton disabled={isDisabled}
                                    printText="In giấy từ chối"
                                    tooltipText="Giấy cam kết từ chối sử dụng dịch vụ khám bệnh, chữa bệnh"
                                >
                                    <PrintRefusa
                                        customer={d}
                                        data={{}}
                                    />
                                </PrintButton>
                                <Button
                                    onClick={handleReloadData}
                                    icon={<ReloadOutlined />}
                                    loading={isLoading}
                                >
                                    Refresh
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>
            </div>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={24} lg={14}>
                    <Card size="small" bordered style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Họ tên"><strong>{d?.full_name || ''}</strong></Descriptions.Item>
                            <Descriptions.Item label="Nickname">{d?.fb_name || ''}</Descriptions.Item>
                            <Descriptions.Item label="Mã số sổ khám">
                                {
                                    d?.medical_book_number ? (
                                        <Paragraph style={{ margin: 0 }} copyable={
                                            {
                                                text: d ? d.medical_book_number : null,
                                                tooltips: ['Sao chép mã', 'Đã sao chép!'],
                                            }
                                        }>
                                            {d ? d.medical_book_number : null}
                                        </Paragraph>
                                    ) : ("-")
                                }

                            </Descriptions.Item>
                            <Descriptions.Item label="Mã PID">
                                {
                                    d?.invisalignId ? (
                                        <Paragraph style={{ margin: 0 }} copyable={
                                            {
                                                text: d ? d.invisalignId : null,
                                                tooltips: ['Sao chép mã', 'Đã sao chép!'],
                                            }
                                        }>
                                            {d ? d.invisalignId : null}
                                        </Paragraph>
                                    ) : ("-")
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Labo">
                                {d?.labo_names ? (
                                    <>{d?.labo_names}</>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Giới tính">{d ? ConvertcustomerGender(d.gender) : null}</Descriptions.Item>
                            <Descriptions.Item label="Năm sinh / Tuổi">
                                {d.birthday ? (
                                    <Space>
                                        <>{isValidDate(d?.birthday, false)}</>
                                        <Text type="secondary">•</Text>
                                        <>{d.age ? `${d.age} tuổi` : "—"}</>
                                    </Space>
                                ) : "—"}
                            </Descriptions.Item>
                            {/* <Descriptions.Item label="Số điện thoại">
                                {phones.map((item, idx) => (
                                    <span key={item.customer_id + '-' + idx}>
                                        <HandlePhoneNumber
                                            isPrimary={item.is_primary}
                                            showIsPrimary={phones.length > 1}
                                            permission={permission}
                                            inputPinCode={inputPinCode}
                                            customer_id={item.customer_id}
                                            hiddenPhoneNumber={repeatPhoneNumber(item.phone)}
                                            fullPhoneNumber={item.phone}
                                        />
                                        {idx < phones.length - 1 && <>&nbsp;-&nbsp;</>}
                                    </span>
                                ))}
                                {phones.length > 1 &&
                                    <>
                                        <Divider type='vertical' />
                                        <Button size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => setIsOpenPhoneList(true)}
                                            type="dashed" className='color-blue'
                                        //disabled={!checkPermission('customer', 'set_primary_phone')}
                                        >
                                            Cập nhật số chính
                                        </Button>
                                    </>
                                }
                            </Descriptions.Item> */}
                            <Descriptions.Item label="Số điện thoại">
                                {noPhone ? (
                                    <span className="color-gray">
                                        {d?.other_contact}
                                    </span>
                                ) : phones.length > 0 ? (
                                    <>
                                        {phones.map((item, idx) => (
                                            <span key={item.customer_id + '-' + idx}>
                                                <HandlePhoneNumber
                                                    isPrimary={item.is_primary}
                                                    showIsPrimary={phones.length > 1}
                                                    permission={permission}
                                                    inputPinCode={inputPinCode}
                                                    customer_id={item.customer_id}
                                                    hiddenPhoneNumber={repeatPhoneNumber(item.phone)}
                                                    fullPhoneNumber={item.phone}
                                                />
                                                {idx < phones.length - 1 && <>&nbsp;-&nbsp;</>}
                                            </span>
                                        ))}

                                        {phones.length > 1 && (
                                            <>
                                                <Divider type="vertical" />
                                                <Button
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => setIsOpenPhoneList(true)}
                                                    type="dashed"
                                                    className="color-blue"
                                                >
                                                    Cập nhật số chính
                                                </Button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <span className="color-gray">
                                        {d?.other_contact}
                                    </span>
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Địa chỉ">
                                {d?.address || '-'}, {d?.hamlet || '-'}, {d?.wards_name || '-'},  {d?.province_name || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Cơ sở">{d?.branch_name || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Nguồn khách hàng">
                                {d.customer_source || "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ghi chú y khoa">
                                {d.conditions &&
                                    <Text type="danger">
                                        <PlusCircleTwoTone twoToneColor="red" />&nbsp;
                                        {d.conditions}
                                    </Text>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ghi chú">
                                {d ? HtmlParser(d.note) : "-"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card
                        size="small"
                        bordered
                        style={{
                            marginBottom: 16,
                            paddingTop: 0,
                        }}
                        title={<span style={{ fontWeight: 600 }}><RetweetOutlined /> Tương tác</span>}
                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Trạng thái">
                                {
                                    d.customer_status_color ? (
                                        <Tag color={d.customer_status_color}>{d ? d.customer_status : null}</Tag>
                                    ) : (
                                        <Tag>{d ? d.customer_status : null}</Tag>
                                    )
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Loại tương tác">
                                <>{d ? d.fb_Interactive_type : null}</>
                            </Descriptions.Item>
                            <Descriptions.Item label="Trạng thái tương tác">
                                <>{d ? d.fb_Interactive_status : null}</>
                            </Descriptions.Item>
                            <Descriptions.Item label="Link facebook">
                                {d ? d.fb_link : null}
                            </Descriptions.Item>
                            <Descriptions.Item label="Link fanpage">
                                {d ? d.fb_source : null}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card
                        size="small"
                        bordered
                        style={{
                            marginBottom: 16,
                            paddingTop: 0,
                        }}
                        bodyStyle={{ padding: 0 }}
                        title={<span style={{ fontWeight: 600 }}><UsergroupAddOutlined /> Thông tin người liên quan</span>}
                    >
                        <FamilyRelationshipsTable data={d.family_relationships} />
                    </Card>
                </Col>

                <Col xs={24} md={24} lg={10}>
                    <Card
                        size="small"
                        title={<><DollarOutlined /> Thông tin tài chính</>}
                        bordered
                        style={{ marginBottom: 16 }}
                        extra={
                            canViewFinance && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={showMoney ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                    onClick={() => setShowMoney(!showMoney)}
                                >
                                    {showMoney ? 'Ẩn' : 'Hiện'}
                                </Button>
                            )
                        }

                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Doanh số">
                                <Tag color='green'>
                                    {showMoney
                                        ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.revenue} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                        : '••••••••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Ưu đãi">
                                <Tag color='blue'>
                                    {showMoney
                                        ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.total_discount} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                        : '••••••••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Đã thu">
                                <Tag color='cyan'>
                                    {showMoney
                                        ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.total_amount_paid} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                        : '••••••••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Tỷ lệ thu">
                                <Tag color={collectRateColor}>
                                    {showMoney ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {paidPercent}%
                                        </span>
                                    ) : '•••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Còn nợ">
                                <Tag color='red'>
                                    {showMoney
                                        ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.customer_debt} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                        : '••••••••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Dịch vụ chưa làm">
                                <Tag color='olive'>
                                    {showMoney
                                        ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.revenue_not_done} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                        : '••••••••'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Chi phí bổ sung">
                                <Tooltip placement="topLeft" title="Click để cập nhật">
                                    <span className='item-action-btn' onClick={() => onDetailAdditionalCost(d?.id)}>
                                        <Tag color='orange'>
                                            {showMoney
                                                ? <NumberFormat style={{ fontWeight: 600, fontSize: 16 }} value={d?.additional_cost_total} displayType={'text'} suffix="đ" thousandSeparator={true} />
                                                : '••••••••'}
                                        </Tag>
                                        <EditOutlined />
                                    </span>
                                </Tooltip>
                            </Descriptions.Item>

                            <Descriptions.Item label="Hình thức thanh toán">
                                <Tooltip placement="topLeft" title="Click để cập nhật">
                                    <span className='item-action-btn' onClick={() => onDetailPaymentMethod(d?.id)}>
                                        {d?.latest_customer_payment?.payment_method && (
                                            <>
                                                {RenderPaymentMethods(d?.latest_customer_payment?.payment_method)}
                                                {showMoney && (
                                                    <>
                                                        <NumberFormat
                                                            value={d?.latest_customer_payment?.monthly_payment || 0}
                                                            displayType={'text'}
                                                            suffix="đ"
                                                            thousandSeparator={true}
                                                        />/tháng&nbsp;
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <EditOutlined />
                                    </span>
                                </Tooltip>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    <Card size="small" title={<><CalendarOutlined /> Lịch hẹn gần nhất</>} bordered style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Ngày giờ">
                                {firstAppointment?.time ? isValidDate(firstAppointment.time, true) : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Nội dung">
                                <strong>{ConvertCustomerScheduleType(firstAppointment.name)}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Ghi chú">
                                {firstAppointment?.note ? HtmlParser(firstAppointment.note) : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Địa điểm">
                                {firstAppointment?.branch_names || '—'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    <Card size="small" title={<><CreditCardOutlined /> Mã định danh & Thẻ</>} bordered style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Mã số thẻ cũ">
                                {
                                    d?.card_number ? (
                                        <Paragraph style={{ margin: 0 }} copyable={
                                            {
                                                text: d ? d.card_number : null,
                                                tooltips: ['Sao chép mã', 'Đã sao chép!'],
                                            }
                                        }>
                                            {d ? d.card_number : null}
                                        </Paragraph>
                                    ) : ("-")
                                }

                            </Descriptions.Item>
                            <Descriptions.Item label="Mã số thẻ mới">
                                {
                                    d?.card_number_new ? (
                                        <Paragraph style={{ margin: 0 }} copyable={
                                            {
                                                text: d ? d.card_number_new : null,
                                                tooltips: ['Sao chép mã', 'Đã sao chép!'],
                                            }
                                        }>
                                            {d ? d.card_number_new : null}
                                        </Paragraph>
                                    ) : ("-")
                                }
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
                <Col span={24}>
                    <div style={{ fontStyle: 'italic' }}>
                        <strong>Ngày tạo:</strong> {isValidDate(d.created_at)}{' '}
                        bởi:{''}
                        {created_user && created_user.full_name && (
                            <span style={{ marginLeft: 8 }}>
                                {created_user.user_role?.name && (
                                    <span style={{ color: "#999", fontSize: 13.5 }}>
                                        {created_user.user_role.name}:{' '}
                                    </span>
                                )}
                                <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                                    {created_user.full_name}
                                </span>
                            </span>
                        )}
                    </div>
                </Col>
            </Row>
            <TreatmentSessionForm customerId={d.id} visible={formVisible} onCancel={handleCancel} />
            <CustomerEditForm
                visible={isOpenEditForm}
                currentItem={d}
                reloadData={handleReloadData}
                onCancel={() => toggleOpenEditForm(false)}
            />
            <CustomerPayment
                customer_id={d?.id}
                data={customerPaymentData}
                reloadCustomer={handleReloadData}
                reloadCustomerPayment={() => onDetailPaymentMethod(d?.id)}
                onCancel={() => setIsOpenPaymentMethod(false)}
                visible={isOpenPaymentMethod}
            />

            <AdditionalCost
                customer_id={d?.id}
                data={additionalCostData}
                reloadCustomer={handleReloadData}
                reloadAdditionalCost={() => onDetailAdditionalCost(d?.id)}
                onCancel={() => setIsOpenAdditionalCost(false)}
                visible={isOpenAdditionalCost}
            />

            <PhoneList
                data={d?.phones}
                reloadCustomer={handleReloadData}
                onCancel={() => setIsOpenPhoneList(false)}
                visible={isOpenPhoneList}
            />
        </div >
    );
}
